import { useState, useEffect } from 'react';
import { 
  Save, 
  Globe, 
  CheckCircle2, 
  AlertCircle,
  FileText,
  Settings,
  Plus,
  Trash2,
  Layout,
  Home,
  Package,
  Building2,
  Target,
  Award,
  Sparkles
} from 'lucide-react';
import { contentService } from '../services/contentService';

const pageTypes = [
  { id: 'hero', label: '首页 Hero', icon: Home },
  { id: 'products', label: '产品模块', icon: Package },
  { id: 'about', label: '关于我们', icon: Building2 },
  { id: 'scenarios', label: '应用场景', icon: Target },
  { id: 'advantages', label: '核心优势', icon: Award },
  { id: 'seo', label: 'SEO 配置', icon: Sparkles },
];

const defaultContents: Record<string, any> = {
  hero: {
    title: '船运发货',
    subtitle: '一码通系统',
    description: '郑州博乐为企业打造的全流程船运物流管理平台，实现从派车调度到计量结算的数字化智能化管理。支持煤炭、化工、水泥、矿山、粮油等行业的智慧物流解决方案。',
    badge: '智能化船运物流解决方案',
    cta_primary: '了解产品',
    cta_secondary: '预约演示',
    stats: [
      { value: '99.9%', label: '系统稳定性' },
      { value: '50%+', label: '效率提升' },
      { value: '1000+', label: '服务企业' },
    ],
  },
  products: {
    title: '全方位船运物流管理',
    subtitle: '产品模块',
    description: '从派船调度到计量结算，从自助审核到数据决策，构建完整的船运发货数字化管理体系',
    products: [
      {
        id: 'dispatch',
        icon: 'Truck',
        title: '派单平台',
        subtitle: '智能派单调度',
        description: '基于订单需求的智能派单系统，实现船舶资源的最优配置，支持在线预约、自助派单、实时跟踪等功能。',
        features: ['在线预约派船', '智能调度算法', '船舶实时定位', '船主管理', '运费结算'],
        color: 'from-ocean-400 to-ocean-600',
        bgColor: 'bg-ocean-50',
        iconColor: 'text-ocean-600',
      },
      {
        id: 'scheduling',
        icon: 'Ship',
        title: '船运调度系统',
        subtitle: '船舶调度管理',
        description: '全面的船舶调度管理平台，实现船舶进出港管理、泊位调度、电子报港等核心功能，提升港口作业效率。',
        features: ['船舶进出港管理', '泊位智能调度', '电子报港', '船期管理', '港口作业协同'],
        color: 'from-primary-400 to-primary-600',
        bgColor: 'bg-primary-50',
        iconColor: 'text-primary-600',
      },
      {
        id: 'weighing',
        icon: 'Scale',
        title: '船运计量系统',
        subtitle: '精准计量管理',
        description: '集成多种计量方式，实现装货前、装货中、装货后的全程计量管控。',
        features: ['料斗秤集成', '自助计量采集', '计量异常预警', '计量报表分析'],
        color: 'from-emerald-400 to-emerald-600',
        bgColor: 'bg-emerald-50',
        iconColor: 'text-emerald-600',
      },
      {
        id: 'printing',
        icon: 'Printer',
        title: '自助审核打印系统',
        subtitle: '无纸化办公',
        description: '支持提货单自助审核、电子签名、二维码打印等功能，实现发货流程的数字化和无纸化管理。',
        features: ['自助提货审核', '电子签名认证', '二维码提货单', '自动打印服务', '单据电子归档'],
        color: 'from-amber-400 to-amber-600',
        bgColor: 'bg-amber-50',
        iconColor: 'text-amber-600',
      },
      {
        id: 'dashboard',
        icon: 'BarChart3',
        title: '管理驾驶舱',
        subtitle: '数据可视化',
        description: '直观的数据可视化大屏，实时展示发货量、运输效率、库存状态等关键指标，辅助管理决策。',
        features: ['实时数据大屏', '多维度分析报表', '预警提醒推送', '移动端查看', '数据导出分享'],
        color: 'from-violet-400 to-violet-600',
        bgColor: 'bg-violet-50',
        iconColor: 'text-violet-600',
      },
    ],
  },
  about: {
    company_name: '郑州博乐信息技术有限公司',
    tagline: '用科技赋能物流，让大宗物资运输更智能、更高效',
    description: '郑州博乐信息技术有限公司创立于2007年，是一家专注于企业数智化供应链物流管理方案的高新技术企业。当前业务已覆盖化工、水泥、矿山、粮食、建筑、煤炭、制造、焦化、食品、电厂、有色金属等10余行业。',
    highlights: [
      {
        icon: 'Building2',
        title: '企业定位',
        description: '专注于企业数智化供应链物流管理，是国内领先的企业物流数智化解决方案提供商',
      },
      {
        icon: 'Target',
        title: '核心业务',
        description: '一码通管理系统、运输管理（TMS）、 无人值守称重管理、车辆智能排队管理、智能鹤管装车、危化品停车场管理、颗粒物/散物自动定量装车、智能调度管理',
      },
      {
        icon: 'Users',
        title: '服务保障',
        description: '提供管理咨询、方案设计、系统建设、运营制度优化、现场实施、售后运维一站式服务',
      },
      {
        icon: 'Award',
        title: '技术实力',
        description: '拥有自主知识产权的物流管理平台，获得多项软件著作权和技术专利',
      },
    ],
    stats: [
      { value: '19+', label: '年行业经验' },
      { value: '1000+家', label: '服务企业' },
      { value: '150+', label: '覆盖城市' },
      { value: '99.9%', label: '系统稳定性' },
    ],
  },
  scenarios: {
    title: '多行业船运发货解决方案',
    subtitle: '应用场景',
    description: '深耕建材行业，为水泥、熟料、石子等产品提供专业的船运发货管理服务',
    scenarios: [
      {
        id: 'cement',
        icon: 'Factory',
        title: '水泥船运发货',
        subtitle: 'Cement Shipping',
        description: '针对水泥产品特性，提供从订单下达到船舶装货的全流程管理，支持散装水泥和袋装水泥的差异化管理。',
        stats: [
          { label: '日均发货量', value: '5000+', unit: '吨' },
          { label: '装货效率', value: '30%', unit: '提升' },
          { label: '等待时间', value: '40%', unit: '减少' },
        ],
        features: [
          { icon: 'QrCode', title: '扫码装货', desc: '一码通行，快速装货' },
          { icon: 'FileCheck', title: '自助计量', desc: '精准计量，实时上传' },
          { icon: 'TrendingUp', title: '效率提升', desc: '流程优化，效率倍增' },
          { icon: 'Clock', title: '实时监控', desc: '全程可视，实时掌控' },
        ],
        color: 'from-slate-500 to-slate-700',
        bgColor: 'bg-slate-50',
      },
      {
        id: 'aggregate',
        icon: 'Boxes',
        title: '砂石骨料船运',
        subtitle: 'Aggregate Shipping',
        description: '骨料船运发货管理，支持多种规格砂石骨料的分类管理和装船作业，满足不同客户的定制化需求。',
        stats: [
          { label: '日发货船次', value: '100+', unit: '船次' },
          { label: '品种管理', value: '20+', unit: '种' },
          { label: '客户满意度', value: '98%', unit: '' },
        ],
        features: [
          { icon: 'Boxes', title: '多品种管理', desc: '分类管理，清晰明了' },
          { icon: 'QrCode', title: '扫码识别', desc: '快速识别，准确无误' },
          { icon: 'TrendingUp', title: '数据分析', desc: '数据驱动，智能决策' },
          { icon: 'Clock', title: '快速结算', desc: '自助结算，实时到账' },
        ],
        color: 'from-stone-500 to-stone-700',
        bgColor: 'bg-stone-50',
      },
      {
        id: 'coal',
        icon: 'Flame',
        title: '煤炭船运发货',
        subtitle: 'Coal Shipping',
        description: '煤炭大宗物资船运管理，支持多港口调度和船舶配载优化，确保运输安全高效。',
        stats: [
          { label: '单船装载量', value: '5000+', unit: '吨' },
          { label: '调度效率', value: '50%', unit: '提升' },
          { label: '运输安全', value: '99.9%', unit: '' },
        ],
        features: [
          { icon: 'Ship', title: '大船调度', desc: '专业调度，高效配载' },
          { icon: 'FileCheck', title: '料斗秤计量', desc: '专业计量，准确可靠' },
          { icon: 'TrendingUp', title: '效率优化', desc: '智能调度，提升效率' },
          { icon: 'Clock', title: '全程跟踪', desc: '实时跟踪，安全到达' },
        ],
        color: 'from-amber-600 to-amber-800',
        bgColor: 'bg-amber-50',
      },
      {
        id: 'grain',
        icon: 'Wheat',
        title: '粮食船运发货',
        subtitle: 'Grain Shipping',
        description: '粮食作物船运管理，支持散粮和袋装粮食的分类运输，保障粮食运输过程中的品质和安全。',
        stats: [
          { label: '日吞吐量', value: '3000+', unit: '吨' },
          { label: '损耗降低', value: '80%', unit: '' },
          { label: '品质保障', value: '100%', unit: '' },
        ],
        features: [
          { icon: 'Boxes', title: '分类管理', desc: '品种分类，防混装' },
          { icon: 'QrCode', title: '溯源管理', desc: '全程溯源，保品质' },
          { icon: 'TrendingUp', title: '智能调度', desc: '优化配载，降成本' },
          { icon: 'Clock', title: '快速通关', desc: '自助审核，提效率' },
        ],
        color: 'from-emerald-500 to-emerald-700',
        bgColor: 'bg-emerald-50',
      },
    ],
  },
  advantages: {
    title: '为什么选择我们',
    subtitle: '核心优势',
    description: '多年行业深耕，打造专业、智能、可靠的船运发货管理系统',
    advantages: [
      {
        icon: 'Cpu',
        title: '智能化管理',
        subtitle: 'AI-Powered',
        description: '运用人工智能和大数据技术，实现智能调度、智能预警、智能决策，让管理更高效。',
        highlights: ['智能调度算法', 'AI异常检测', '预测性分析', '自动化流程'],
        color: 'from-blue-400 to-blue-600',
      },
      {
        icon: 'Shield',
        title: '安全可靠',
        subtitle: 'Secure & Reliable',
        description: '采用银行级数据加密技术，多重备份机制，确保数据安全不丢失，系统稳定运行。',
        highlights: ['数据加密传输', '多重备份机制', '权限分级管理', '操作日志追踪'],
        color: 'from-emerald-400 to-emerald-600',
      },
      {
        icon: 'Zap',
        title: '高效便捷',
        subtitle: 'Efficient & Convenient',
        description: '一码通行，全程无纸化，大幅提升作业效率，减少人工干预，降低运营成本。',
        highlights: ['扫码快速通行', '无纸化办公', '移动化操作', '实时同步'],
        color: 'from-amber-400 to-amber-600',
      },
      {
        icon: 'BarChart3',
        title: '数据可视',
        subtitle: 'Data Visualization',
        description: '直观的数据大屏和报表，多维度数据分析，助力管理者快速掌握业务动态。',
        highlights: ['实时数据大屏', '多维度报表', '自定义分析', '数据导出'],
        color: 'from-violet-400 to-violet-600',
      },
      {
        icon: 'Users',
        title: '协同作业',
        subtitle: 'Collaborative Work',
        description: '打通上下游环节，实现供应商、运输方、客户等多方协同，信息实时共享。',
        highlights: ['多方协同', '信息实时共享', '消息即时推送', '在线沟通'],
        color: 'from-pink-400 to-pink-600',
      },
      {
        icon: 'Headphones',
        title: '专业服务',
        subtitle: 'Professional Service',
        description: '7x24小时技术支持，专业实施团队，提供从咨询到上线的全程服务保障。',
        highlights: ['7x24小时支持', '专业实施团队', '定期培训', '持续升级'],
        color: 'from-cyan-400 to-cyan-600',
      },
    ],
  },
  seo: {
    title: '船运发货一码通系统 - 郑州博乐',
    description: '郑州博乐船运发货一码通系统，为企业提供全流程船运物流管理平台。支持煤炭、化工、水泥、矿山、粮油等行业智慧物流解决方案，实现从派船调度到计量结算的数字化管理。',
    keywords: '船运发货,一码通系统,船运物流,智慧物流,博乐软件,大宗物资运输,船舶调度,船运计量',
    author: '郑州博乐信息技术有限公司',
  },
};

export default function ContentManager() {
  const [activeTab, setActiveTab] = useState('hero');
  const [content, setContent] = useState<any>(defaultContents.hero);
  const [originalContent, setOriginalContent] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [lastPublished, setLastPublished] = useState<string | null>(null);

  useEffect(() => {
    loadContent(activeTab);
  }, [activeTab]);

  const loadContent = async (pageType: string) => {
    setLoading(true);
    try {
      const data = await contentService.getPublishedContent(pageType);
      if (data && data.content) {
        setContent(data.content);
        setOriginalContent(data.content);
        setLastPublished(data.published_at);
      } else {
        const defaultData = defaultContents[pageType] || {};
        setContent(defaultData);
        setOriginalContent(null);
        setLastPublished(null);
      }
    } catch (error) {
      console.error('Error loading content:', error);
      const defaultData = defaultContents[pageType] || {};
      setContent(defaultData);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);
    try {
      await contentService.saveContent(activeTab, content);
      setMessage({ type: 'success', text: '内容已保存（草稿状态）' });
      setOriginalContent(content);
    } catch (error) {
      setMessage({ type: 'error', text: '保存失败，请重试' });
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async () => {
    setPublishing(true);
    setMessage(null);
    try {
      await contentService.publishContent(activeTab);
      setMessage({ type: 'success', text: '发布成功！前台页面已更新' });
      setLastPublished(new Date().toISOString());
    } catch (error) {
      setMessage({ type: 'error', text: '发布失败，请重试' });
    } finally {
      setPublishing(false);
    }
  };

  const hasChanges = JSON.stringify(content) !== JSON.stringify(originalContent);

  const updateField = (field: string, value: any) => {
    setContent((prev: any) => ({ ...prev, [field]: value }));
  };

  const updateArrayItem = (arrayField: string, index: number, field: string, value: any) => {
    setContent((prev: any) => ({
      ...prev,
      [arrayField]: prev[arrayField].map((item: any, i: number) => 
        i === index ? { ...item, [field]: value } : item
      ),
    }));
  };

  const addArrayItem = (arrayField: string, defaultItem: any) => {
    setContent((prev: any) => ({
      ...prev,
      [arrayField]: [...(prev[arrayField] || []), defaultItem],
    }));
  };

  const removeArrayItem = (arrayField: string, index: number) => {
    setContent((prev: any) => ({
      ...prev,
      [arrayField]: prev[arrayField].filter((_: any, i: number) => i !== index),
    }));
  };

  const renderHeroEditor = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">主标题</label>
          <input
            type="text"
            value={content.title || ''}
            onChange={(e) => updateField('title', e.target.value)}
            className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-ocean-500 focus:border-ocean-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">副标题</label>
          <input
            type="text"
            value={content.subtitle || ''}
            onChange={(e) => updateField('subtitle', e.target.value)}
            className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-ocean-500 focus:border-ocean-500"
          />
        </div>
      </div>
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2">描述</label>
        <textarea
          value={content.description || ''}
          onChange={(e) => updateField('description', e.target.value)}
          rows={3}
          className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-ocean-500 focus:border-ocean-500"
        />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">徽章文字</label>
          <input
            type="text"
            value={content.badge || ''}
            onChange={(e) => updateField('badge', e.target.value)}
            className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-ocean-500 focus:border-ocean-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">主按钮文字</label>
          <input
            type="text"
            value={content.cta_primary || ''}
            onChange={(e) => updateField('cta_primary', e.target.value)}
            className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-ocean-500 focus:border-ocean-500"
          />
        </div>
      </div>
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2">次按钮文字</label>
        <input
          type="text"
          value={content.cta_secondary || ''}
          onChange={(e) => updateField('cta_secondary', e.target.value)}
          className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-ocean-500 focus:border-ocean-500"
        />
      </div>
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="block text-sm font-medium text-slate-700">统计数据</label>
          <button
            onClick={() => addArrayItem('stats', { value: '', label: '' })}
            className="text-sm text-ocean-600 hover:text-ocean-700 flex items-center gap-1"
          >
            <Plus className="w-4 h-4" /> 添加
          </button>
        </div>
        <div className="space-y-2">
          {(content.stats || []).map((stat: any, index: number) => (
            <div key={index} className="flex gap-2">
              <input
                type="text"
                value={stat.value}
                onChange={(e) => updateArrayItem('stats', index, 'value', e.target.value)}
                placeholder="数值"
                className="flex-1 px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-ocean-500"
              />
              <input
                type="text"
                value={stat.label}
                onChange={(e) => updateArrayItem('stats', index, 'label', e.target.value)}
                placeholder="标签"
                className="flex-1 px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-ocean-500"
              />
              <button
                onClick={() => removeArrayItem('stats', index)}
                className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderProductsEditor = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">页面标题</label>
          <input
            type="text"
            value={content.title || ''}
            onChange={(e) => updateField('title', e.target.value)}
            className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-ocean-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">副标题</label>
          <input
            type="text"
            value={content.subtitle || ''}
            onChange={(e) => updateField('subtitle', e.target.value)}
            className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-ocean-500"
          />
        </div>
      </div>
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2">页面描述</label>
        <textarea
          value={content.description || ''}
          onChange={(e) => updateField('description', e.target.value)}
          rows={2}
          className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-ocean-500"
        />
      </div>
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="block text-sm font-medium text-slate-700">产品列表</label>
          <button
            onClick={() => addArrayItem('products', {
              id: `product_${Date.now()}`,
              icon: 'Truck',
              title: '新产品',
              subtitle: '产品副标题',
              description: '产品描述',
              features: ['功能1', '功能2'],
              color: 'from-ocean-400 to-ocean-600',
              bgColor: 'bg-ocean-50',
              iconColor: 'text-ocean-600',
            })}
            className="text-sm text-ocean-600 hover:text-ocean-700 flex items-center gap-1"
          >
            <Plus className="w-4 h-4" /> 添加产品
          </button>
        </div>
        <div className="space-y-4">
          {(content.products || []).map((product: any, index: number) => (
            <div key={product.id} className="border border-slate-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <span className="font-medium text-slate-700">产品 {index + 1}</span>
                <button
                  onClick={() => removeArrayItem('products', index)}
                  className="text-red-500 hover:bg-red-50 p-1 rounded"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <input
                  type="text"
                  value={product.title}
                  onChange={(e) => updateArrayItem('products', index, 'title', e.target.value)}
                  placeholder="产品名称"
                  className="px-3 py-2 border border-slate-200 rounded-lg text-sm"
                />
                <input
                  type="text"
                  value={product.subtitle}
                  onChange={(e) => updateArrayItem('products', index, 'subtitle', e.target.value)}
                  placeholder="副标题"
                  className="px-3 py-2 border border-slate-200 rounded-lg text-sm"
                />
              </div>
              <textarea
                value={product.description}
                onChange={(e) => updateArrayItem('products', index, 'description', e.target.value)}
                placeholder="产品描述"
                rows={2}
                className="w-full mt-3 px-3 py-2 border border-slate-200 rounded-lg text-sm"
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderAboutEditor = () => (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2">公司名称</label>
        <input
          type="text"
          value={content.company_name || ''}
          onChange={(e) => updateField('company_name', e.target.value)}
          className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-ocean-500"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2">标语</label>
        <input
          type="text"
          value={content.tagline || ''}
          onChange={(e) => updateField('tagline', e.target.value)}
          className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-ocean-500"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2">公司简介</label>
        <textarea
          value={content.description || ''}
          onChange={(e) => updateField('description', e.target.value)}
          rows={4}
          className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-ocean-500"
        />
      </div>
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="block text-sm font-medium text-slate-700">统计数据</label>
          <button
            onClick={() => addArrayItem('stats', { value: '', label: '' })}
            className="text-sm text-ocean-600 hover:text-ocean-700 flex items-center gap-1"
          >
            <Plus className="w-4 h-4" /> 添加
          </button>
        </div>
        <div className="space-y-2">
          {(content.stats || []).map((stat: any, index: number) => (
            <div key={index} className="flex gap-2">
              <input
                type="text"
                value={stat.value}
                onChange={(e) => updateArrayItem('stats', index, 'value', e.target.value)}
                placeholder="数值"
                className="flex-1 px-4 py-2 border border-slate-200 rounded-lg"
              />
              <input
                type="text"
                value={stat.label}
                onChange={(e) => updateArrayItem('stats', index, 'label', e.target.value)}
                placeholder="标签"
                className="flex-1 px-4 py-2 border border-slate-200 rounded-lg"
              />
              <button
                onClick={() => removeArrayItem('stats', index)}
                className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderScenariosEditor = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">页面标题</label>
          <input
            type="text"
            value={content.title || ''}
            onChange={(e) => updateField('title', e.target.value)}
            className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-ocean-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">副标题</label>
          <input
            type="text"
            value={content.subtitle || ''}
            onChange={(e) => updateField('subtitle', e.target.value)}
            className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-ocean-500"
          />
        </div>
      </div>
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2">页面描述</label>
        <textarea
          value={content.description || ''}
          onChange={(e) => updateField('description', e.target.value)}
          rows={2}
          className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-ocean-500"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2">应用场景列表（JSON格式）</label>
        <textarea
          value={JSON.stringify(content.scenarios || [], null, 2)}
          onChange={(e) => {
            try {
              const parsed = JSON.parse(e.target.value);
              updateField('scenarios', parsed);
            } catch (err) {
              // 忽略解析错误
            }
          }}
          rows={10}
          className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-ocean-500 font-mono text-sm"
        />
        <p className="text-xs text-slate-500 mt-1">提示：场景数据较复杂，建议直接编辑JSON</p>
      </div>
    </div>
  );

  const renderAdvantagesEditor = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">页面标题</label>
          <input
            type="text"
            value={content.title || ''}
            onChange={(e) => updateField('title', e.target.value)}
            className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-ocean-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">副标题</label>
          <input
            type="text"
            value={content.subtitle || ''}
            onChange={(e) => updateField('subtitle', e.target.value)}
            className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-ocean-500"
          />
        </div>
      </div>
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2">页面描述</label>
        <textarea
          value={content.description || ''}
          onChange={(e) => updateField('description', e.target.value)}
          rows={2}
          className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-ocean-500"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2">优势列表（JSON格式）</label>
        <textarea
          value={JSON.stringify(content.advantages || [], null, 2)}
          onChange={(e) => {
            try {
              const parsed = JSON.parse(e.target.value);
              updateField('advantages', parsed);
            } catch (err) {
              // 忽略解析错误
            }
          }}
          rows={10}
          className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-ocean-500 font-mono text-sm"
        />
        <p className="text-xs text-slate-500 mt-1">提示：优势数据较复杂，建议直接编辑JSON</p>
      </div>
    </div>
  );

  const renderSEOEditor = () => (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2">页面标题 (Title)</label>
        <input
          type="text"
          value={content.title || ''}
          onChange={(e) => updateField('title', e.target.value)}
          className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-ocean-500"
        />
        <p className="text-xs text-slate-500 mt-1">建议控制在 60 个字符以内</p>
      </div>
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2">页面描述 (Description)</label>
        <textarea
          value={content.description || ''}
          onChange={(e) => updateField('description', e.target.value)}
          rows={3}
          className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-ocean-500"
        />
        <p className="text-xs text-slate-500 mt-1">建议控制在 160 个字符以内</p>
      </div>
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2">关键词 (Keywords)</label>
        <input
          type="text"
          value={content.keywords || ''}
          onChange={(e) => updateField('keywords', e.target.value)}
          className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-ocean-500"
        />
        <p className="text-xs text-slate-500 mt-1">多个关键词用英文逗号分隔</p>
      </div>
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2">作者/公司</label>
        <input
          type="text"
          value={content.author || ''}
          onChange={(e) => updateField('author', e.target.value)}
          className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-ocean-500"
        />
      </div>
    </div>
  );

  const renderEditor = () => {
    switch (activeTab) {
      case 'hero':
        return renderHeroEditor();
      case 'products':
        return renderProductsEditor();
      case 'about':
        return renderAboutEditor();
      case 'scenarios':
        return renderScenariosEditor();
      case 'advantages':
        return renderAdvantagesEditor();
      case 'seo':
        return renderSEOEditor();
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-3">
            <Layout className="w-7 h-7 text-ocean-600" />
            内容发布管理
          </h1>
          <p className="text-slate-600 mt-2">
            编辑网站内容，保存后点击"一键发布"即可更新前台页面，无需重新部署
          </p>
        </div>

        <div className="grid lg:grid-cols-4 gap-6">
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
              <div className="p-4 bg-slate-50 border-b border-slate-200">
                <h3 className="font-semibold text-slate-700 flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  页面选择
                </h3>
              </div>
              <div className="p-2">
                {pageTypes.map((page) => {
                  const IconComponent = page.icon;
                  return (
                    <button
                      key={page.id}
                      onClick={() => setActiveTab(page.id)}
                      className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-colors ${
                        activeTab === page.id
                          ? 'bg-ocean-50 text-ocean-700 border border-ocean-200'
                          : 'text-slate-600 hover:bg-slate-50'
                      }`}
                    >
                      <IconComponent className={`w-5 h-5 ${activeTab === page.id ? 'text-ocean-600' : 'text-slate-400'}`} />
                      <span className="font-medium">{page.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {lastPublished && (
              <div className="mt-4 bg-emerald-50 rounded-xl p-4 border border-emerald-200">
                <div className="flex items-center gap-2 text-emerald-700 mb-1">
                  <CheckCircle2 className="w-4 h-4" />
                  <span className="font-medium text-sm">已发布</span>
                </div>
                <p className="text-xs text-emerald-600">
                  最后发布: {new Date(lastPublished).toLocaleString('zh-CN')}
                </p>
              </div>
            )}
          </div>

          <div className="lg:col-span-3">
            <div className="bg-white rounded-xl shadow-sm border border-slate-200">
              <div className="p-6 border-b border-slate-200">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-lg font-semibold text-slate-900">
                      {pageTypes.find(p => p.id === activeTab)?.label} 编辑
                    </h2>
                    <p className="text-sm text-slate-500 mt-1">
                      编辑完成后保存草稿，确认无误后点击发布
                    </p>
                  </div>
                  <div className="flex gap-3">
                    <button
                      onClick={handleSave}
                      disabled={saving || !hasChanges}
                      className="flex items-center gap-2 px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Save className="w-4 h-4" />
                      {saving ? '保存中...' : '保存草稿'}
                    </button>
                    <button
                      onClick={handlePublish}
                      disabled={publishing}
                      className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-ocean-500 to-primary-600 text-white rounded-lg hover:shadow-lg transition-all disabled:opacity-50"
                    >
                      <Globe className="w-4 h-4" />
                      {publishing ? '发布中...' : '一键发布'}
                    </button>
                  </div>
                </div>
              </div>

              <div className="p-6">
                {message && (
                  <div className={`mb-6 p-4 rounded-lg flex items-center gap-3 ${
                    message.type === 'success'
                      ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                      : 'bg-red-50 text-red-700 border border-red-200'
                  }`}>
                    {message.type === 'success' ? (
                      <CheckCircle2 className="w-5 h-5" />
                    ) : (
                      <AlertCircle className="w-5 h-5" />
                    )}
                    <span>{message.text}</span>
                  </div>
                )}

                {loading ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="w-8 h-8 border-4 border-ocean-200 border-t-ocean-500 rounded-full animate-spin" />
                  </div>
                ) : (
                  renderEditor()
                )}
              </div>
            </div>

            <div className="mt-6 bg-blue-50 rounded-xl p-4 border border-blue-200">
              <h4 className="font-medium text-blue-900 mb-2 flex items-center gap-2">
                <Settings className="w-4 h-4" />
                使用说明
              </h4>
              <ul className="text-sm text-blue-700 space-y-1">
                <li>1. 选择左侧页面类型进行编辑</li>
                <li>2. 编辑完成后点击"保存草稿"保存内容</li>
                <li>3. 确认无误后点击"一键发布"更新前台页面</li>
                <li>4. 发布后用户刷新页面即可看到最新内容</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
